import { useRef, useCallback } from 'react';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || window.location.origin;
const CLIP_DURATION = 30000; // 30 seconds recording after motion

export function useRecorder({ camId, camName }) {
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timeoutRef = useRef(null);

  const startRecording = useCallback((stream) => {
    if (recorderRef.current?.state === 'recording') return;

    chunksRef.current = [];

    const options = { mimeType: 'video/webm;codecs=vp8,opus' };
    try {
      recorderRef.current = new MediaRecorder(stream, options);
    } catch {
      recorderRef.current = new MediaRecorder(stream);
    }

    recorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorderRef.current.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      await uploadClip(blob);
    };

    recorderRef.current.start(1000); // collect chunks every second

    // Stop after CLIP_DURATION
    timeoutRef.current = setTimeout(() => {
      stopRecording();
    }, CLIP_DURATION);

    console.log('[Recorder] Started');
  }, [camId, camName]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
      console.log('[Recorder] Stopped');
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const captureSnapshot = useCallback(async (videoElement) => {
    // Wait a moment to ensure we get a real frame
    await new Promise(resolve => setTimeout(resolve, 500));
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth || 640;
    canvas.height = videoElement.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoElement, 0, 0);

    canvas.toBlob(async (blob) => {
      const form = new FormData();
      form.append('snapshot', blob, 'snapshot.jpg');
      form.append('camId', camId);
      form.append('camName', camName);

      try {
        await fetch(`${SERVER_URL}/api/upload-snapshot`, {
          method: 'POST',
          body: form,
        });
        console.log('[Snapshot] Sent to Telegram');
      } catch (err) {
        console.error('[Snapshot] Error:', err);
      }
    }, 'image/jpeg', 0.85);
  }, [camId, camName]);

  async function uploadClip(blob) {
    const form = new FormData();
    form.append('clip', blob, `motion_${Date.now()}.webm`);
    form.append('camId', camId);
    form.append('camName', camName);
    form.append('timestamp', new Date().toISOString());

    try {
      const res = await fetch(`${SERVER_URL}/api/upload-clip`, {
        method: 'POST',
        body: form,
      });
      const data = await res.json();
      console.log('[Clip] Uploaded:', data.fileName);
    } catch (err) {
      console.error('[Clip] Upload error:', err);
    }
  }

  return { startRecording, stopRecording, captureSnapshot };
}
